import { useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

import { agentGraphSchema } from '@repo/shared-types';

import type { AgentGraph } from '@repo/shared-types';

export const ConversationFlowPanel = ({
  graph,
  onChange,
}: {
  graph: AgentGraph;
  onChange: (graph: AgentGraph) => void;
}) => {
  const id = useId();
  const [draft, setDraft] = useState(() =>
    JSON.stringify(
      graph.conversation_flow ?? {
        enabled: false,
        version: '1',
        modules: [],
        actions: [],
        handoff_requirements: {},
      },
      null,
      2,
    ),
  );
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const apply = () => {
    try {
      const parsed = agentGraphSchema.safeParse({ ...graph, conversation_flow: JSON.parse(draft) });
      if (!parsed.success) {
        setError(parsed.error.issues.map((issue) => issue.message).join('; '));
        return;
      }
      onChange({ ...graph, conversation_flow: parsed.data.conversation_flow });
      setError('');
      setStatus('Policy applied to the form. Save the configuration to persist it.');
    } catch {
      setError('Enter valid JSON. The previous policy has not been changed.');
    }
  };

  return (
    <section className="bg-card space-y-3 rounded-xl border p-5" aria-labelledby={`${id}-title`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 id={`${id}-title`} className="font-semibold">
            Shared conversation progress — voice only
          </h3>
          <p className="text-muted-foreground text-sm">
            Remember confirmed details, track modules and resume interrupted requests. Requires
            shared Redis storage.
          </p>
        </div>
        <Switch
          aria-label="Enable shared voice conversation progress"
          checked={graph.conversation_flow?.enabled ?? false}
          disabled={!graph.conversation_flow}
          onCheckedChange={(enabled) => {
            if (graph.conversation_flow)
              onChange({ ...graph, conversation_flow: { ...graph.conversation_flow, enabled } });
          }}
        />
      </div>
      <details>
        <summary className="cursor-pointer rounded py-2 focus-visible:outline-2">
          Edit module policy (JSON)
        </summary>
        <p className="text-muted-foreground mb-2 text-sm">
          Add and validate a policy before enabling. Module owners must match graph agent names.
          Changes affect new calls only; text agents are unchanged.
        </p>
        <label htmlFor={`${id}-json`} className="text-sm font-medium">
          Conversation policy
        </label>
        <textarea
          id={`${id}-json`}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setStatus('');
          }}
          rows={12}
          className="bg-background focus-visible:ring-ring mt-1 w-full rounded-md border p-3 font-mono text-sm focus-visible:ring-2"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        {error && (
          <p id={`${id}-error`} role="alert" className="text-destructive mt-2 text-sm">
            {error}
          </p>
        )}
        <Button type="button" variant="outline" onClick={apply} className="mt-3">
          Validate and apply to form
        </Button>
      </details>
      {status && (
        <p role="status" className="text-muted-foreground text-sm">
          {status}
        </p>
      )}
    </section>
  );
};
